import type {
  CampaignData,
  CollectionKey,
  CollectionItem,
  NewCollectionItem,
  User,
  AuthResponse,
  ExportData,
  PlayerCharacter,
  FactionRelationship,
  SessionLinkedItem,
  CampaignSummary,
  CampaignMember,
  CampaignMemberProvisionResult,
  CampaignAccessPolicy,
  RegistrationAllowlistEntry,
  EntityShare,
  ShareGroup,
  CampaignFeatureFlagState,
  RelationshipDirection,
  RelationshipEdge,
  WikiBacklinkPage,
  WikiTemplate,
  ContentRevision,
  ContentRevisionDiff,
  ContentRestoreResponse,
  SessionAgendaItem,
  SessionPrepTask,
  SessionEncounter,
  SessionRecapItem,
  CampaignCastMember,
  StatblockEntry,
  StatblockEntryVersion,
  StatblockVersionRestoreResponse,
  EncounterStatblockLink,
  GmScreenPin,
  WorldMap,
  MapLayer,
  MapMarker,
  Timeline,
  TimelineEvent,
  ChroniclePlaybackResponse,
  FamilyTree,
  FamilyTreeNode,
  FamilyTreeEdge,
  DiplomacyWeb,
  DiplomacyWebNode,
  DiplomacyWebEdge,
  Whiteboard,
  WhiteboardNode,
  WhiteboardEdge,
  ContentTree,
  ContentTreeNode,
  CardToWikiConversionResult,
  ParityModuleStatus,
  ParityScaffoldList,
  Ruleset,
  RuleSource,
  CompendiumEntry,
  CompendiumImportPayload,
  CompendiumImportResult,
  CharacterAbility,
  CharacterCondition,
  CharacterInventoryItem,
  CharacterResource,
  CharacterSheet,
  CharacterSheetRuntime,
  CharacterValidationSummary,
  CharacterSpell,
  EncounterTemplate,
  EncounterTemplateDifficultySummary,
  CombatSession,
  CombatSessionReplayResponse,
  CombatEvent,
  Combatant,
  DiceRoll,
  IntegrationProvider,
  CampaignWebhook,
  CampaignApiKey,
  CollaborationActivityEvent,
  EntityComment,
  NotificationSubscription,
  PublishSpace,
  PublicPublishLanding,
  PublicPublishRouteView,
  PublicationRoute,
  PublishActivity,
  AssistantCitation,
  AssistantThread,
  AssistantThreadAction,
  AssistantThreadMessage,
  KnowledgeDocument,
  KnowledgeChunk,
  OracleSSEEvent,
} from '../../shared/types';

const API_BASE = '/api';

type AssistantMessage = {
  role: 'user' | 'assistant' | 'system';
  content: string;
};

export type AssistantToolCallResult = {
  id: string;
  name: string;
  arguments: Record<string, unknown>;
};

export type AssistantChatOptions = {
  /** Opt-in to server-side system prompt with campaign context. */
  useServerContext?: boolean;
  /** Current tab for context-aware responses. */
  activeTab?: string;
  /** Link conversation to a persistent server-side thread. */
  threadId?: number;
  /** Enable function calling for structured actions. */
  useTools?: boolean;
  /** P2-9: Live GM mode for in-session support. */
  gmMode?: boolean;
  /** P3-13: Context visibility mode. */
  contextMode?: 'gm' | 'player' | 'shared';
};

export type WorkflowInfo = {
  id: string;
  name: string;
  description: string;
  stepCount: number;
};

export type WorkflowStepEvent = {
  type: 'step_start' | 'step_complete' | 'workflow_complete' | 'error';
  stepId?: string;
  label?: string;
  content?: string;
  index?: number;
  workflowId?: string;
  summary?: string;
  error?: string;
};

export type EntityExtractionResult = {
  npcs: Array<{ name: string; description: string; location: string; notes: string }>;
  locations: Array<{ name: string; description: string; connections: string; notes: string }>;
  organizations: Array<{ name: string; type: string; goals: string; notes: string }>;
  plotHooks: Array<{ title: string; description: string; priority: string }>;
  items: Array<{ name: string; description: string; significance: string }>;
  sourceText: string;
};

export type NLSearchResult = {
  entityType: string;
  entityId: number;
  name: string;
  relevance: 'high' | 'medium' | 'low';
  matchReason: string;
  snippet: string;
};

export type NLSearchResponse = {
  query: string;
  results: NLSearchResult[];
  synthesis: string;
};

export type AssistantChatResult = {
  content: string;
  toolCalls: AssistantToolCallResult[];
  threadId?: number;
};

export type AssistantStreamResult = {
  content: string;
  toolCalls: AssistantToolCallResult[];
  threadId?: number;
};

export type ContextPin = {
  id: number;
  entityType: string;
  entityId: number;
  pinnedAt: string;
};

type CompendiumFacetResponse = {
  entryTypes: Array<{ entryType: string; count: number }>;
  rulesets: Array<{ rulesetId: number; rulesetName: string; count: number }>;
  sources: Array<{ sourceId: number; sourceTitle: string; count: number }>;
};

type CompendiumQueryResponse = ParityScaffoldList<CompendiumEntry> & {
  query: {
    q: string;
    entryType: string;
    rulesetId: number | null;
    sourceId: number | null;
    limit: number;
    offset: number;
  };
  facets: CompendiumFacetResponse;
};

type CombatSessionDetailResponse = CombatSession & {
  combatants: Combatant[];
  events: CombatEvent[];
};

class ApiClient {
  private token: string | null = null;

  setToken(token: string | null): void {
    this.token = token;
    if (token) {
      localStorage.setItem('auth-token', token);
    } else {
      localStorage.removeItem('auth-token');
    }
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('auth-token');
    }
    return this.token;
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    let response: Response;
    try {
      response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers,
      });
    } catch {
      // Queue mutations when offline
      const method = (options.method || 'GET').toUpperCase();
      if (!navigator.onLine && (method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE')) {
        const { enqueueMutation } = await import('../utils/offlineQueue');
        await enqueueMutation({
          method,
          url: `${API_BASE}${endpoint}`,
          body: typeof options.body === 'string' ? options.body : null,
          timestamp: Date.now(),
        });
        throw new Error('Saved offline — will sync when connected.');
      }
      throw new Error('Cannot connect to server. Please ensure the backend is running.');
    }

    if (response.status === 401) {
      this.setToken(null);
      throw new Error('Unauthorized');
    }

    if (response.status === 204) {
      return undefined as T;
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Server error' }));
      throw new Error(error.error || 'API Error');
    }

    return response.json();
  }

  // Auth methods
  async login(username: string, password: string): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    this.setToken(response.token);
    return response;
  }

  async register(username: string, password: string): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    this.setToken(response.token);
    return response;
  }

  logout(): void {
    this.setToken(null);
  }

  async getCurrentUser(): Promise<{ user: User }> {
    return this.request<{ user: User }>('/auth/me');
  }

  // Campaign access methods
  async listCampaigns(): Promise<{ activeCampaignId: number | null; campaigns: CampaignSummary[] }> {
    return this.request<{ activeCampaignId: number | null; campaigns: CampaignSummary[] }>('/campaigns');
  }

  async selectCampaign(campaignId: number): Promise<{ activeCampaignId: number }> {
    return this.request<{ activeCampaignId: number }>(`/campaigns/${campaignId}/select`, {
      method: 'POST',
    });
  }

  async getFeatureFlags(): Promise<CampaignFeatureFlagState[]> {
    return this.request<CampaignFeatureFlagState[]>('/campaign/feature-flags');
  }

  async setFeatureFlag(
    key: string,
    enabled: boolean
  ): Promise<CampaignFeatureFlagState> {
    return this.request<CampaignFeatureFlagState>(`/campaign/feature-flags/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    });
  }

  async getRulesModuleStatus(): Promise<ParityModuleStatus> {
    return this.request<ParityModuleStatus>('/rules/health');
  }

  async getRulesetsScaffold(): Promise<ParityScaffoldList<Ruleset>> {
    return this.request<ParityScaffoldList<Ruleset>>('/rules/rulesets');
  }

  async getRulesCompendiumScaffold(
    params: {
      q?: string;
      entryType?: string;
      rulesetId?: number;
      sourceId?: number;
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<CompendiumQueryResponse> {
    const query = new URLSearchParams();
    if (params.q) query.set('q', params.q);
    if (params.entryType) query.set('entryType', params.entryType);
    if (typeof params.rulesetId === 'number') query.set('rulesetId', String(params.rulesetId));
    if (typeof params.sourceId === 'number') query.set('sourceId', String(params.sourceId));
    if (typeof params.limit === 'number') query.set('limit', String(params.limit));
    if (typeof params.offset === 'number') query.set('offset', String(params.offset));
    const suffix = query.size > 0 ? `?${query.toString()}` : '';
    return this.request<CompendiumQueryResponse>(`/rules/compendium${suffix}`);
  }

  async createRuleset(input: {
    slug?: string;
    name: string;
    version?: string;
    publisher?: string;
    isOfficial?: boolean;
    metadata?: Record<string, unknown>;
  }): Promise<Ruleset> {
    return this.request<Ruleset>('/rules/rulesets', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateRuleset(
    rulesetId: number,
    updates: {
      slug?: string;
      name?: string;
      version?: string;
      publisher?: string;
      isOfficial?: boolean;
      metadata?: Record<string, unknown>;
    }
  ): Promise<Ruleset> {
    return this.request<Ruleset>(`/rules/rulesets/${rulesetId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteRuleset(rulesetId: number): Promise<void> {
    return this.request<void>(`/rules/rulesets/${rulesetId}`, {
      method: 'DELETE',
    });
  }

  async getRuleSources(rulesetId?: number): Promise<ParityScaffoldList<RuleSource>> {
    const suffix = typeof rulesetId === 'number' ? `?rulesetId=${rulesetId}` : '';
    return this.request<ParityScaffoldList<RuleSource>>(`/rules/sources${suffix}`);
  }

  async createRuleSource(
    rulesetId: number,
    input: {
      sourceKey: string;
      title: string;
      license?: string;
      metadata?: Record<string, unknown>;
    }
  ): Promise<RuleSource> {
    return this.request<RuleSource>(`/rules/rulesets/${rulesetId}/sources`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async createCompendiumEntry(input: {
    rulesetId: number;
    sourceId?: number | null;
    entryType: string;
    slug?: string;
    name: string;
    contentMarkdown?: string;
    metadata?: Record<string, unknown>;
  }): Promise<CompendiumEntry> {
    return this.request<CompendiumEntry>('/rules/compendium', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async importCompendiumEntries(payload: CompendiumImportPayload): Promise<CompendiumImportResult> {
    return this.request<CompendiumImportResult>('/rules/compendium/import', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateCompendiumEntry(
    entryId: number,
    updates: {
      rulesetId?: number;
      sourceId?: number | null;
      entryType?: string;
      slug?: string;
      name?: string;
      contentMarkdown?: string;
      metadata?: Record<string, unknown>;
    }
  ): Promise<CompendiumEntry> {
    return this.request<CompendiumEntry>(`/rules/compendium/${entryId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteCompendiumEntry(entryId: number): Promise<void> {
    return this.request<void>(`/rules/compendium/${entryId}`, {
      method: 'DELETE',
    });
  }

  async getCharacterSheetsModuleStatus(): Promise<ParityModuleStatus> {
    return this.request<ParityModuleStatus>('/characters/health');
  }

  async getCharacterSheets(): Promise<ParityScaffoldList<CharacterSheet>> {
    return this.request<ParityScaffoldList<CharacterSheet>>('/characters/sheets');
  }

  async getCharacterSheetsScaffold(): Promise<ParityScaffoldList<CharacterSheet>> {
    return this.getCharacterSheets();
  }

  async getCharacterSheet(sheetId: number): Promise<CharacterSheet> {
    return this.request<CharacterSheet>(`/characters/sheets/${sheetId}`);
  }

  async createCharacterSheet(input: {
    name: string;
    level?: number;
    visibility?: 'public' | 'players' | 'gm' | 'share_group';
    status?: 'active' | 'retired' | 'draft';
    notes?: string;
    rulesetId?: number | null;
    metadata?: Record<string, unknown>;
  }): Promise<CharacterSheet> {
    return this.request<CharacterSheet>('/characters/sheets', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateCharacterSheet(
    sheetId: number,
    updates: {
      name?: string;
      level?: number;
      visibility?: 'public' | 'players' | 'gm' | 'share_group';
      status?: 'active' | 'retired' | 'draft';
      notes?: string;
      rulesetId?: number | null;
      metadata?: Record<string, unknown>;
    }
  ): Promise<CharacterSheet> {
    return this.request<CharacterSheet>(`/characters/sheets/${sheetId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteCharacterSheet(sheetId: number): Promise<void> {
    return this.request<void>(`/characters/sheets/${sheetId}`, {
      method: 'DELETE',
    });
  }

  async getCharacterSheetRuntime(sheetId: number): Promise<CharacterSheetRuntime> {
    return this.request<CharacterSheetRuntime>(`/characters/sheets/${sheetId}/runtime`);
  }

  async validateCharacterSheet(sheetId: number): Promise<CharacterValidationSummary> {
    return this.request<CharacterValidationSummary>(`/characters/sheets/${sheetId}/validate`);
  }

  async getCharacterAbilities(sheetId: number): Promise<ParityScaffoldList<CharacterAbility>> {
    return this.request<ParityScaffoldList<CharacterAbility>>(`/characters/sheets/${sheetId}/abilities`);
  }

  async upsertCharacterAbility(
    sheetId: number,
    abilityKey: string,
    input: {
      score?: number;
      modifier?: number;
    }
  ): Promise<CharacterAbility> {
    return this.request<CharacterAbility>(
      `/characters/sheets/${sheetId}/abilities/${encodeURIComponent(abilityKey)}`,
      {
        method: 'PUT',
        body: JSON.stringify(input),
      }
    );
  }

  async deleteCharacterAbility(sheetId: number, abilityKey: string): Promise<void> {
    return this.request<void>(
      `/characters/sheets/${sheetId}/abilities/${encodeURIComponent(abilityKey)}`,
      {
        method: 'DELETE',
      }
    );
  }

  async getCharacterResources(sheetId: number): Promise<ParityScaffoldList<CharacterResource>> {
    return this.request<ParityScaffoldList<CharacterResource>>(`/characters/sheets/${sheetId}/resources`);
  }

  async upsertCharacterResource(
    sheetId: number,
    resourceKey: string,
    input: {
      currentValue?: number;
      maxValue?: number;
      resetPolicy?: string;
    }
  ): Promise<CharacterResource> {
    return this.request<CharacterResource>(
      `/characters/sheets/${sheetId}/resources/${encodeURIComponent(resourceKey)}`,
      {
        method: 'PUT',
        body: JSON.stringify(input),
      }
    );
  }

  async deleteCharacterResource(sheetId: number, resourceKey: string): Promise<void> {
    return this.request<void>(
      `/characters/sheets/${sheetId}/resources/${encodeURIComponent(resourceKey)}`,
      {
        method: 'DELETE',
      }
    );
  }

  async getCharacterInventoryItems(sheetId: number): Promise<ParityScaffoldList<CharacterInventoryItem>> {
    return this.request<ParityScaffoldList<CharacterInventoryItem>>(`/characters/sheets/${sheetId}/inventory`);
  }

  async createCharacterInventoryItem(
    sheetId: number,
    input: {
      compendiumEntryId?: number | null;
      itemName: string;
      quantity?: number;
      equipped?: boolean;
      notes?: string;
    }
  ): Promise<CharacterInventoryItem> {
    return this.request<CharacterInventoryItem>(`/characters/sheets/${sheetId}/inventory`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateCharacterInventoryItem(
    sheetId: number,
    itemId: number,
    updates: {
      compendiumEntryId?: number | null;
      itemName?: string;
      quantity?: number;
      equipped?: boolean;
      notes?: string;
    }
  ): Promise<CharacterInventoryItem> {
    return this.request<CharacterInventoryItem>(`/characters/sheets/${sheetId}/inventory/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteCharacterInventoryItem(sheetId: number, itemId: number): Promise<void> {
    return this.request<void>(`/characters/sheets/${sheetId}/inventory/${itemId}`, {
      method: 'DELETE',
    });
  }

  async getCharacterSpells(sheetId: number): Promise<ParityScaffoldList<CharacterSpell>> {
    return this.request<ParityScaffoldList<CharacterSpell>>(`/characters/sheets/${sheetId}/spells`);
  }

  async createCharacterSpell(
    sheetId: number,
    input: {
      compendiumEntryId?: number | null;
      spellName: string;
      prepared?: boolean;
      usesRemaining?: number | null;
    }
  ): Promise<CharacterSpell> {
    return this.request<CharacterSpell>(`/characters/sheets/${sheetId}/spells`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateCharacterSpell(
    sheetId: number,
    spellId: number,
    updates: {
      compendiumEntryId?: number | null;
      spellName?: string;
      prepared?: boolean;
      usesRemaining?: number | null;
    }
  ): Promise<CharacterSpell> {
    return this.request<CharacterSpell>(`/characters/sheets/${sheetId}/spells/${spellId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteCharacterSpell(sheetId: number, spellId: number): Promise<void> {
    return this.request<void>(`/characters/sheets/${sheetId}/spells/${spellId}`, {
      method: 'DELETE',
    });
  }

  async getCharacterConditions(sheetId: number): Promise<ParityScaffoldList<CharacterCondition>> {
    return this.request<ParityScaffoldList<CharacterCondition>>(`/characters/sheets/${sheetId}/conditions`);
  }

  async createCharacterCondition(
    sheetId: number,
    input: {
      conditionKey: string;
      source?: string;
      expiresAt?: string | null;
      notes?: string;
    }
  ): Promise<CharacterCondition> {
    return this.request<CharacterCondition>(`/characters/sheets/${sheetId}/conditions`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateCharacterCondition(
    sheetId: number,
    conditionId: number,
    updates: {
      conditionKey?: string;
      source?: string;
      expiresAt?: string | null;
      notes?: string;
    }
  ): Promise<CharacterCondition> {
    return this.request<CharacterCondition>(`/characters/sheets/${sheetId}/conditions/${conditionId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteCharacterCondition(sheetId: number, conditionId: number): Promise<void> {
    return this.request<void>(`/characters/sheets/${sheetId}/conditions/${conditionId}`, {
      method: 'DELETE',
    });
  }

  async restCharacterSheet(sheetId: number, type: 'short' | 'long'): Promise<{ restType: string; resourcesReset: number; derived: Record<string, unknown> }> {
    return this.request(`/characters/sheets/${sheetId}/rest`, {
      method: 'POST',
      body: JSON.stringify({ type }),
    });
  }

  async getEncountersModuleStatus(): Promise<ParityModuleStatus> {
    return this.request<ParityModuleStatus>('/encounters/health');
  }

  async getEncounterTemplates(
    params: {
      includeMonsters?: boolean;
      partyLevels?: number[];
      partySize?: number;
      averageLevel?: number;
      useCampaignCharacters?: boolean;
    } = {}
  ): Promise<ParityScaffoldList<EncounterTemplate>> {
    const query = new URLSearchParams();
    if (typeof params.includeMonsters === 'boolean') {
      query.set('includeMonsters', params.includeMonsters ? 'true' : 'false');
    }
    if (Array.isArray(params.partyLevels) && params.partyLevels.length > 0) {
      query.set('partyLevels', params.partyLevels.join(','));
    }
    if (typeof params.partySize === 'number') {
      query.set('partySize', String(params.partySize));
    }
    if (typeof params.averageLevel === 'number') {
      query.set('averageLevel', String(params.averageLevel));
    }
    if (typeof params.useCampaignCharacters === 'boolean') {
      query.set('useCampaignCharacters', params.useCampaignCharacters ? 'true' : 'false');
    }
    const suffix = query.size > 0 ? `?${query.toString()}` : '';
    return this.request<ParityScaffoldList<EncounterTemplate>>(`/encounters/templates${suffix}`);
  }

  async getEncounterTemplate(templateId: number): Promise<EncounterTemplate> {
    return this.request<EncounterTemplate>(`/encounters/templates/${templateId}`);
  }

  async getEncounterTemplateDifficulty(
    templateId: number,
    params: {
      partyLevels?: number[];
      partySize?: number;
      averageLevel?: number;
      useCampaignCharacters?: boolean;
    } = {}
  ): Promise<EncounterTemplateDifficultySummary> {
    const query = new URLSearchParams();
    if (Array.isArray(params.partyLevels) && params.partyLevels.length > 0) {
      query.set('partyLevels', params.partyLevels.join(','));
    }
    if (typeof params.partySize === 'number') {
      query.set('partySize', String(params.partySize));
    }
    if (typeof params.averageLevel === 'number') {
      query.set('averageLevel', String(params.averageLevel));
    }
    if (typeof params.useCampaignCharacters === 'boolean') {
      query.set('useCampaignCharacters', params.useCampaignCharacters ? 'true' : 'false');
    }
    const suffix = query.size > 0 ? `?${query.toString()}` : '';
    return this.request<EncounterTemplateDifficultySummary>(`/encounters/templates/${templateId}/difficulty${suffix}`);
  }

  async createEncounterTemplate(input: {
    name: string;
    difficultyTarget?: string;
    notes?: string;
    monsters?: Array<{
      compendiumEntryId?: number | null;
      monsterName: string;
      count?: number;
      notes?: string;
    }>;
  }): Promise<EncounterTemplate> {
    return this.request<EncounterTemplate>('/encounters/templates', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateEncounterTemplate(
    templateId: number,
    updates: {
      name?: string;
      difficultyTarget?: string;
      notes?: string;
      monsters?: Array<{
        compendiumEntryId?: number | null;
        monsterName: string;
        count?: number;
        notes?: string;
      }>;
    }
  ): Promise<EncounterTemplate> {
    return this.request<EncounterTemplate>(`/encounters/templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteEncounterTemplate(templateId: number): Promise<void> {
    return this.request<void>(`/encounters/templates/${templateId}`, {
      method: 'DELETE',
    });
  }

  async getCombatSessions(
    params: {
      state?: 'planned' | 'active' | 'paused' | 'completed' | 'abandoned';
      sessionNoteId?: number;
      encounterTemplateId?: number;
      includeCombatants?: boolean;
    } = {}
  ): Promise<ParityScaffoldList<CombatSession>> {
    const query = new URLSearchParams();
    if (params.state) query.set('state', params.state);
    if (typeof params.sessionNoteId === 'number') query.set('sessionNoteId', String(params.sessionNoteId));
    if (typeof params.encounterTemplateId === 'number') query.set('encounterTemplateId', String(params.encounterTemplateId));
    if (typeof params.includeCombatants === 'boolean') {
      query.set('includeCombatants', params.includeCombatants ? 'true' : 'false');
    }
    const suffix = query.size > 0 ? `?${query.toString()}` : '';
    return this.request<ParityScaffoldList<CombatSession>>(`/encounters/combat-sessions${suffix}`);
  }

  async getCombatSession(sessionId: number, eventLimit?: number): Promise<CombatSessionDetailResponse> {
    const suffix = typeof eventLimit === 'number' ? `?eventLimit=${eventLimit}` : '';
    return this.request<CombatSessionDetailResponse>(`/encounters/combat-sessions/${sessionId}${suffix}`);
  }

  async getCombatSessionReplay(sessionId: number): Promise<CombatSessionReplayResponse> {
    return this.request<CombatSessionReplayResponse>(`/encounters/combat-sessions/${sessionId}/replay`);
  }

  async createCombatSession(input: {
    sessionNoteId?: number | null;
    encounterTemplateId?: number | null;
    state?: 'planned' | 'active' | 'paused' | 'completed' | 'abandoned';
    roundNum?: number;
    activeTurn?: number;
    combatants?: Array<{
      entityType?: '' | 'player_characters' | 'npcs' | 'character_sheets' | 'compendium_entries' | 'statblock_entries';
      entityId?: number | null;
      name: string;
      initiative?: number;
      hpCurrent?: number;
      hpMax?: number;
      status?: Record<string, unknown>;
      sortOrder?: number;
    }>;
  }): Promise<CombatSession> {
    return this.request<CombatSession>('/encounters/combat-sessions', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateCombatSession(
    sessionId: number,
    updates: {
      sessionNoteId?: number | null;
      encounterTemplateId?: number | null;
      state?: 'planned' | 'active' | 'paused' | 'completed' | 'abandoned';
      roundNum?: number;
      activeTurn?: number;
    }
  ): Promise<CombatSession> {
    return this.request<CombatSession>(`/encounters/combat-sessions/${sessionId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async runCombatSessionAction(
    sessionId: number,
    action: 'start' | 'pause' | 'resume' | 'complete' | 'abandon' | 'next_turn' | 'reset_turns'
  ): Promise<{ session: CombatSession; event: CombatEvent; combatants: Combatant[] }> {
    return this.request<{ session: CombatSession; event: CombatEvent; combatants: Combatant[] }>(
      `/encounters/combat-sessions/${sessionId}/actions`,
      {
        method: 'POST',
        body: JSON.stringify({ action }),
      }
    );
  }

  async deleteCombatSession(sessionId: number): Promise<void> {
    return this.request<void>(`/encounters/combat-sessions/${sessionId}`, {
      method: 'DELETE',
    });
  }

  async getCombatSessionEvents(
    sessionId: number,
    limit?: number
  ): Promise<ParityScaffoldList<CombatEvent>> {
    const suffix = typeof limit === 'number' ? `?limit=${limit}` : '';
    return this.request<ParityScaffoldList<CombatEvent>>(`/encounters/combat-sessions/${sessionId}/events${suffix}`);
  }

  async createCombatant(
    sessionId: number,
    input: {
      entityType?: '' | 'player_characters' | 'npcs' | 'character_sheets' | 'compendium_entries' | 'statblock_entries';
      entityId?: number | null;
      name: string;
      initiative?: number;
      hpCurrent?: number;
      hpMax?: number;
      status?: Record<string, unknown>;
      sortOrder?: number;
    }
  ): Promise<Combatant> {
    return this.request<Combatant>(`/encounters/combat-sessions/${sessionId}/combatants`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateCombatant(
    sessionId: number,
    combatantId: number,
    updates: {
      entityType?: '' | 'player_characters' | 'npcs' | 'character_sheets' | 'compendium_entries' | 'statblock_entries';
      entityId?: number | null;
      name?: string;
      initiative?: number;
      hpCurrent?: number;
      hpMax?: number;
      status?: Record<string, unknown>;
      sortOrder?: number;
    }
  ): Promise<Combatant> {
    return this.request<Combatant>(`/encounters/combat-sessions/${sessionId}/combatants/${combatantId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteCombatant(sessionId: number, combatantId: number): Promise<void> {
    return this.request<void>(`/encounters/combat-sessions/${sessionId}/combatants/${combatantId}`, {
      method: 'DELETE',
    });
  }

  async getDiceRolls(
    params: { combatSessionId?: number; limit?: number } = {}
  ): Promise<ParityScaffoldList<DiceRoll>> {
    const query = new URLSearchParams();
    if (typeof params.combatSessionId === 'number') query.set('combatSessionId', String(params.combatSessionId));
    if (typeof params.limit === 'number') query.set('limit', String(params.limit));
    const suffix = query.size > 0 ? `?${query.toString()}` : '';
    return this.request<ParityScaffoldList<DiceRoll>>(`/encounters/dice-rolls${suffix}`);
  }

  async createDiceRoll(input: {
    combatSessionId?: number | null;
    notation: string;
    result?: Record<string, unknown>;
  }): Promise<DiceRoll> {
    return this.request<DiceRoll>('/encounters/dice-rolls', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async getEncounterTemplatesScaffold(): Promise<ParityScaffoldList<EncounterTemplate>> {
    return this.getEncounterTemplates({ includeMonsters: true });
  }

  async getCombatSessionsScaffold(): Promise<ParityScaffoldList<CombatSession>> {
    return this.getCombatSessions({ includeCombatants: true });
  }

  async getPublishingModuleStatus(): Promise<ParityModuleStatus> {
    return this.request<ParityModuleStatus>('/publish/health');
  }

  async getPublishSpaces(): Promise<ParityScaffoldList<PublishSpace>> {
    return this.request<ParityScaffoldList<PublishSpace>>('/publish/spaces');
  }

  async createPublishSpace(input: {
    name: string;
    slug?: string;
    description?: string;
    visibility?: 'public' | 'players' | 'gm' | 'share_group';
    releaseState?: 'draft' | 'published' | 'archived';
    isPublic?: boolean;
    metadata?: Record<string, unknown>;
  }): Promise<PublishSpace> {
    return this.request<PublishSpace>('/publish/spaces', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updatePublishSpace(
    spaceId: number,
    updates: {
      name?: string;
      slug?: string;
      description?: string;
      visibility?: 'public' | 'players' | 'gm' | 'share_group';
      releaseState?: 'draft' | 'published' | 'archived';
      isPublic?: boolean;
      metadata?: Record<string, unknown>;
    }
  ): Promise<PublishSpace> {
    return this.request<PublishSpace>(`/publish/spaces/${spaceId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deletePublishSpace(spaceId: number): Promise<void> {
    return this.request<void>(`/publish/spaces/${spaceId}`, {
      method: 'DELETE',
    });
  }

  async getPublishActivity(
    params: { publishSpaceId?: number; limit?: number; offset?: number } = {}
  ): Promise<
    ParityScaffoldList<PublishActivity> & {
      query: {
        publishSpaceId: number | null;
        limit: number;
        offset: number;
      };
    }
  > {
    const query = new URLSearchParams();
    if (typeof params.publishSpaceId === 'number') query.set('publishSpaceId', String(params.publishSpaceId));
    if (typeof params.limit === 'number') query.set('limit', String(params.limit));
    if (typeof params.offset === 'number') query.set('offset', String(params.offset));
    const suffix = query.size > 0 ? `?${query.toString()}` : '';
    return this.request<
      ParityScaffoldList<PublishActivity> & {
        query: {
          publishSpaceId: number | null;
          limit: number;
          offset: number;
        };
      }
    >(`/publish/activity${suffix}`);
  }

  async getPublishSpacesScaffold(): Promise<ParityScaffoldList<PublishSpace>> {
    return this.getPublishSpaces();
  }

  async getPublishActivityScaffold(): Promise<ParityScaffoldList<PublishActivity>> {
    return this.getPublishActivity();
  }

  async getPublicPublishLanding(campaignId: number, spaceSlug: string): Promise<PublicPublishLanding> {
    return this.request<PublicPublishLanding>(
      `/publish/public/campaigns/${campaignId}/spaces/${encodeURIComponent(spaceSlug)}`
    );
  }

  async getPublicPublishRoute(
    campaignId: number,
    spaceSlug: string,
    routePath: string
  ): Promise<PublicPublishRouteView> {
    const normalizedPath = routePath
      .split('/')
      .map((segment) => segment.trim())
      .filter((segment) => segment.length > 0)
      .map((segment) => encodeURIComponent(segment))
      .join('/');

    return this.request<PublicPublishRouteView>(
      `/publish/public/campaigns/${campaignId}/spaces/${encodeURIComponent(spaceSlug)}/routes/${normalizedPath}`
    );
  }

  async getPublicationRoutes(
    params: {
      publishSpaceId?: number;
      releaseState?: 'draft' | 'published' | 'archived';
      entityType?: string;
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<
    ParityScaffoldList<PublicationRoute> & {
      query: {
        publishSpaceId: number | null;
        releaseState: string;
        entityType: string;
        limit: number;
        offset: number;
      };
    }
  > {
    const query = new URLSearchParams();
    if (typeof params.publishSpaceId === 'number') query.set('publishSpaceId', String(params.publishSpaceId));
    if (params.releaseState) query.set('releaseState', params.releaseState);
    if (params.entityType) query.set('entityType', params.entityType);
    if (typeof params.limit === 'number') query.set('limit', String(params.limit));
    if (typeof params.offset === 'number') query.set('offset', String(params.offset));
    const suffix = query.size > 0 ? `?${query.toString()}` : '';
    return this.request<
      ParityScaffoldList<PublicationRoute> & {
        query: {
          publishSpaceId: number | null;
          releaseState: string;
          entityType: string;
          limit: number;
          offset: number;
        };
      }
    >(`/publish/routes${suffix}`);
  }

  async createPublicationRoute(input: {
    publishSpaceId: number;
    path: string;
    entityType: string;
    entityId: number;
    releaseState?: 'draft' | 'published' | 'archived';
    metadata?: Record<string, unknown>;
  }): Promise<PublicationRoute> {
    return this.request<PublicationRoute>('/publish/routes', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updatePublicationRoute(
    routeId: number,
    updates: {
      publishSpaceId?: number;
      path?: string;
      entityType?: string;
      entityId?: number;
      releaseState?: 'draft' | 'published' | 'archived';
      metadata?: Record<string, unknown>;
    }
  ): Promise<PublicationRoute> {
    return this.request<PublicationRoute>(`/publish/routes/${routeId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deletePublicationRoute(routeId: number): Promise<void> {
    return this.request<void>(`/publish/routes/${routeId}`, {
      method: 'DELETE',
    });
  }

  async getEntityComments(
    params: { entityType?: string; entityId?: number; limit?: number; offset?: number } = {}
  ): Promise<
    ParityScaffoldList<EntityComment> & {
      query: {
        entityType: string;
        entityId: number | null;
        limit: number;
        offset: number;
      };
    }
  > {
    const query = new URLSearchParams();
    if (params.entityType) query.set('entityType', params.entityType);
    if (typeof params.entityId === 'number') query.set('entityId', String(params.entityId));
    if (typeof params.limit === 'number') query.set('limit', String(params.limit));
    if (typeof params.offset === 'number') query.set('offset', String(params.offset));
    const suffix = query.size > 0 ? `?${query.toString()}` : '';
    return this.request<
      ParityScaffoldList<EntityComment> & {
        query: {
          entityType: string;
          entityId: number | null;
          limit: number;
          offset: number;
        };
      }
    >(`/publish/comments${suffix}`);
  }

  async createEntityComment(input: {
    entityType: string;
    entityId: number;
    body: string;
    metadata?: Record<string, unknown>;
  }): Promise<EntityComment> {
    return this.request<EntityComment>('/publish/comments', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateEntityComment(
    commentId: number,
    updates: {
      body?: string;
      metadata?: Record<string, unknown>;
    }
  ): Promise<EntityComment> {
    return this.request<EntityComment>(`/publish/comments/${commentId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteEntityComment(commentId: number): Promise<void> {
    return this.request<void>(`/publish/comments/${commentId}`, {
      method: 'DELETE',
    });
  }

  async getCollaborationActivity(
    params: {
      eventType?: string;
      entityType?: string;
      entityId?: number;
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<
    ParityScaffoldList<CollaborationActivityEvent> & {
      query: {
        eventType: string;
        entityType: string;
        entityId: number | null;
        limit: number;
        offset: number;
      };
    }
  > {
    const query = new URLSearchParams();
    if (params.eventType) query.set('eventType', params.eventType);
    if (params.entityType) query.set('entityType', params.entityType);
    if (typeof params.entityId === 'number') query.set('entityId', String(params.entityId));
    if (typeof params.limit === 'number') query.set('limit', String(params.limit));
    if (typeof params.offset === 'number') query.set('offset', String(params.offset));
    const suffix = query.size > 0 ? `?${query.toString()}` : '';
    return this.request<
      ParityScaffoldList<CollaborationActivityEvent> & {
        query: {
          eventType: string;
          entityType: string;
          entityId: number | null;
          limit: number;
          offset: number;
        };
      }
    >(`/publish/collaboration/activity${suffix}`);
  }

  async getNotificationSubscriptions(
    params: {
      userId?: number;
      channel?: 'in_app' | 'email' | 'webhook';
      activeOnly?: boolean;
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<
    ParityScaffoldList<NotificationSubscription> & {
      query: {
        userId: number | null;
        channel: string;
        activeOnly: boolean;
        limit: number;
        offset: number;
      };
    }
  > {
    const query = new URLSearchParams();
    if (typeof params.userId === 'number') query.set('userId', String(params.userId));
    if (params.channel) query.set('channel', params.channel);
    if (typeof params.activeOnly === 'boolean') query.set('activeOnly', params.activeOnly ? 'true' : 'false');
    if (typeof params.limit === 'number') query.set('limit', String(params.limit));
    if (typeof params.offset === 'number') query.set('offset', String(params.offset));
    const suffix = query.size > 0 ? `?${query.toString()}` : '';
    return this.request<
      ParityScaffoldList<NotificationSubscription> & {
        query: {
          userId: number | null;
          channel: string;
          activeOnly: boolean;
          limit: number;
          offset: number;
        };
      }
    >(`/publish/notification-subscriptions${suffix}`);
  }

  async createNotificationSubscription(input: {
    userId?: number;
    channel?: 'in_app' | 'email' | 'webhook';
    eventFilter?: Record<string, unknown>;
    isActive?: boolean;
  }): Promise<NotificationSubscription> {
    return this.request<NotificationSubscription>('/publish/notification-subscriptions', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateNotificationSubscription(
    subscriptionId: number,
    updates: {
      userId?: number;
      channel?: 'in_app' | 'email' | 'webhook';
      eventFilter?: Record<string, unknown>;
      isActive?: boolean;
    }
  ): Promise<NotificationSubscription> {
    return this.request<NotificationSubscription>(`/publish/notification-subscriptions/${subscriptionId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteNotificationSubscription(subscriptionId: number): Promise<void> {
    return this.request<void>(`/publish/notification-subscriptions/${subscriptionId}`, {
      method: 'DELETE',
    });
  }

  async streamOracleGenerate(
    goal: string,
    onEvent: (event: OracleSSEEvent) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const token = this.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}/assistant/oracle/generate`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ goal }),
      signal,
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Failed to start Oracle generation');
    }

    if (!response.body) {
      throw new Error('No response body');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        let newlineIndex = buffer.indexOf('\n');
        while (newlineIndex !== -1) {
          const rawLine = buffer.slice(0, newlineIndex).replace(/\r$/, '');
          buffer = buffer.slice(newlineIndex + 1);

          if (rawLine.startsWith('data: ')) {
            const data = rawLine.slice(6).trim();
            if (data && data !== '[DONE]') {
              try {
                const parsed = JSON.parse(data) as OracleSSEEvent;
                onEvent(parsed);
              } catch {
                // Ignore non-JSON lines
              }
            }
          }

          newlineIndex = buffer.indexOf('\n');
        }
      }

      const remaining = (buffer + decoder.decode()).trim();
      if (remaining.startsWith('data: ')) {
        const data = remaining.slice(6).trim();
        if (data && data !== '[DONE]') {
          try {
            const parsed = JSON.parse(data) as OracleSSEEvent;
            onEvent(parsed);
          } catch {
            // Ignore
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  async getAssistantMemoryModuleStatus(): Promise<ParityModuleStatus> {
    return this.request<ParityModuleStatus>('/assistant/memory/health');
  }

  async getKnowledgeDocuments(
    params: { q?: string; status?: string; limit?: number; offset?: number } = {}
  ): Promise<
    ParityScaffoldList<KnowledgeDocument> & {
      query: {
        q: string;
        status: string;
        limit: number;
        offset: number;
      };
    }
  > {
    const query = new URLSearchParams();
    if (params.q) query.set('q', params.q);
    if (params.status) query.set('status', params.status);
    if (typeof params.limit === 'number') query.set('limit', String(params.limit));
    if (typeof params.offset === 'number') query.set('offset', String(params.offset));
    const suffix = query.size > 0 ? `?${query.toString()}` : '';
    return this.request<
      ParityScaffoldList<KnowledgeDocument> & {
        query: {
          q: string;
          status: string;
          limit: number;
          offset: number;
        };
      }
    >(`/assistant/memory/documents${suffix}`);
  }

  async getKnowledgeDocument(documentId: number): Promise<KnowledgeDocument> {
    return this.request<KnowledgeDocument>(`/assistant/memory/documents/${documentId}`);
  }

  async createKnowledgeDocument(input: {
    title: string;
    sourceType?: string;
    sourceRef?: string;
    content: string;
    metadata?: Record<string, unknown>;
  }): Promise<KnowledgeDocument> {
    return this.request<KnowledgeDocument>('/assistant/memory/documents', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateKnowledgeDocument(
    documentId: number,
    updates: {
      title?: string;
      sourceType?: string;
      sourceRef?: string;
      content?: string;
      status?: 'ready' | 'processing' | 'failed' | 'archived';
      metadata?: Record<string, unknown>;
      rechunk?: boolean;
    }
  ): Promise<KnowledgeDocument> {
    return this.request<KnowledgeDocument>(`/assistant/memory/documents/${documentId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteKnowledgeDocument(documentId: number): Promise<void> {
    return this.request<void>(`/assistant/memory/documents/${documentId}`, {
      method: 'DELETE',
    });
  }

  async getKnowledgeChunks(
    params: { documentId?: number; q?: string; limit?: number; offset?: number } = {}
  ): Promise<
    ParityScaffoldList<KnowledgeChunk> & {
      query: {
        documentId: number | null;
        q: string;
        limit: number;
        offset: number;
      };
    }
  > {
    const query = new URLSearchParams();
    if (typeof params.documentId === 'number') query.set('documentId', String(params.documentId));
    if (params.q) query.set('q', params.q);
    if (typeof params.limit === 'number') query.set('limit', String(params.limit));
    if (typeof params.offset === 'number') query.set('offset', String(params.offset));
    const suffix = query.size > 0 ? `?${query.toString()}` : '';
    return this.request<
      ParityScaffoldList<KnowledgeChunk> & {
        query: {
          documentId: number | null;
          q: string;
          limit: number;
          offset: number;
        };
      }
    >(`/assistant/memory/chunks${suffix}`);
  }

  async getAssistantThreads(
    params: { q?: string; limit?: number; offset?: number } = {}
  ): Promise<
    ParityScaffoldList<AssistantThread> & {
      query: {
        q: string;
        limit: number;
        offset: number;
      };
    }
  > {
    const query = new URLSearchParams();
    if (params.q) query.set('q', params.q);
    if (typeof params.limit === 'number') query.set('limit', String(params.limit));
    if (typeof params.offset === 'number') query.set('offset', String(params.offset));
    const suffix = query.size > 0 ? `?${query.toString()}` : '';
    return this.request<
      ParityScaffoldList<AssistantThread> & {
        query: {
          q: string;
          limit: number;
          offset: number;
        };
      }
    >(`/assistant/memory/threads${suffix}`);
  }

  async getAssistantThread(threadId: number): Promise<AssistantThread & {
    messages: AssistantThreadMessage[];
    actions: AssistantThreadAction[];
  }> {
    return this.request<AssistantThread & {
      messages: AssistantThreadMessage[];
      actions: AssistantThreadAction[];
    }>(`/assistant/memory/threads/${threadId}`);
  }

  async createAssistantThread(input: {
    title: string;
    metadata?: Record<string, unknown>;
  }): Promise<AssistantThread> {
    return this.request<AssistantThread>('/assistant/memory/threads', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateAssistantThread(
    threadId: number,
    updates: {
      title?: string;
      metadata?: Record<string, unknown>;
    }
  ): Promise<AssistantThread> {
    return this.request<AssistantThread>(`/assistant/memory/threads/${threadId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteAssistantThread(threadId: number): Promise<void> {
    return this.request<void>(`/assistant/memory/threads/${threadId}`, {
      method: 'DELETE',
    });
  }

  async getAssistantThreadMessages(
    threadId: number,
    params: {
      role?: 'user' | 'assistant' | 'system';
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<
    ParityScaffoldList<AssistantThreadMessage> & {
      query: {
        role: string;
        limit: number;
        offset: number;
      };
    }
  > {
    const query = new URLSearchParams();
    if (params.role) query.set('role', params.role);
    if (typeof params.limit === 'number') query.set('limit', String(params.limit));
    if (typeof params.offset === 'number') query.set('offset', String(params.offset));
    const suffix = query.size > 0 ? `?${query.toString()}` : '';
    return this.request<
      ParityScaffoldList<AssistantThreadMessage> & {
        query: {
          role: string;
          limit: number;
          offset: number;
        };
      }
    >(`/assistant/memory/threads/${threadId}/messages${suffix}`);
  }

  async createAssistantThreadMessage(
    threadId: number,
    input: {
      role?: 'user' | 'assistant' | 'system';
      content: string;
      citations?: AssistantCitation[];
      metadata?: Record<string, unknown>;
    }
  ): Promise<AssistantThreadMessage> {
    return this.request<AssistantThreadMessage>(`/assistant/memory/threads/${threadId}/messages`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async getAssistantThreadActions(
    threadId: number,
    params: {
      status?: 'queued' | 'running' | 'completed' | 'failed';
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<
    ParityScaffoldList<AssistantThreadAction> & {
      query: {
        status: string;
        limit: number;
        offset: number;
      };
    }
  > {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (typeof params.limit === 'number') query.set('limit', String(params.limit));
    if (typeof params.offset === 'number') query.set('offset', String(params.offset));
    const suffix = query.size > 0 ? `?${query.toString()}` : '';
    return this.request<
      ParityScaffoldList<AssistantThreadAction> & {
        query: {
          status: string;
          limit: number;
          offset: number;
        };
      }
    >(`/assistant/memory/threads/${threadId}/actions${suffix}`);
  }

  async askAssistantThread(
    threadId: number,
    input: {
      content: string;
      documentId?: number;
      maxCitations?: number;
    }
  ): Promise<{
    userMessage: AssistantThreadMessage;
    assistantMessage: AssistantThreadMessage;
    action: AssistantThreadAction;
    citations: AssistantCitation[];
  }> {
    return this.request<{
      userMessage: AssistantThreadMessage;
      assistantMessage: AssistantThreadMessage;
      action: AssistantThreadAction;
      citations: AssistantCitation[];
    }>(`/assistant/memory/threads/${threadId}/ask`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async getIntegrationsModuleStatus(): Promise<ParityModuleStatus> {
    return this.request<ParityModuleStatus>('/integrations/health');
  }

  async getIntegrationProviders(): Promise<ParityScaffoldList<IntegrationProvider>> {
    return this.request<ParityScaffoldList<IntegrationProvider>>('/integrations/providers');
  }

  async getIntegrationWebhooks(
    params: { providerKey?: string; eventKey?: string; activeOnly?: boolean } = {}
  ): Promise<ParityScaffoldList<CampaignWebhook>> {
    const query = new URLSearchParams();
    if (params.providerKey) query.set('providerKey', params.providerKey);
    if (params.eventKey) query.set('eventKey', params.eventKey);
    if (typeof params.activeOnly === 'boolean') {
      query.set('activeOnly', params.activeOnly ? 'true' : 'false');
    }
    const suffix = query.size > 0 ? `?${query.toString()}` : '';
    return this.request<ParityScaffoldList<CampaignWebhook>>(`/integrations/webhooks${suffix}`);
  }

  async createIntegrationWebhook(input: {
    providerKey?: string;
    name: string;
    targetUrl: string;
    eventKey?: string;
    isActive?: boolean;
    metadata?: Record<string, unknown>;
  }): Promise<CampaignWebhook> {
    return this.request<CampaignWebhook>('/integrations/webhooks', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateIntegrationWebhook(
    webhookId: number,
    updates: {
      providerKey?: string;
      name?: string;
      targetUrl?: string;
      eventKey?: string;
      isActive?: boolean;
      metadata?: Record<string, unknown>;
      rotateSecret?: boolean;
    }
  ): Promise<CampaignWebhook> {
    return this.request<CampaignWebhook>(`/integrations/webhooks/${webhookId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteIntegrationWebhook(webhookId: number): Promise<void> {
    return this.request<void>(`/integrations/webhooks/${webhookId}`, {
      method: 'DELETE',
    });
  }

  async getIntegrationApiKeys(
    params: { activeOnly?: boolean } = {}
  ): Promise<ParityScaffoldList<CampaignApiKey>> {
    const query = new URLSearchParams();
    if (typeof params.activeOnly === 'boolean') {
      query.set('activeOnly', params.activeOnly ? 'true' : 'false');
    }
    const suffix = query.size > 0 ? `?${query.toString()}` : '';
    return this.request<ParityScaffoldList<CampaignApiKey>>(`/integrations/api-keys${suffix}`);
  }

  async createIntegrationApiKey(input: {
    name: string;
    scopes?: string[];
    isActive?: boolean;
    metadata?: Record<string, unknown>;
  }): Promise<CampaignApiKey> {
    return this.request<CampaignApiKey>('/integrations/api-keys', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateIntegrationApiKey(
    apiKeyId: number,
    updates: {
      name?: string;
      scopes?: string[];
      isActive?: boolean;
      metadata?: Record<string, unknown>;
    }
  ): Promise<CampaignApiKey> {
    return this.request<CampaignApiKey>(`/integrations/api-keys/${apiKeyId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async rotateIntegrationApiKey(apiKeyId: number): Promise<CampaignApiKey> {
    return this.request<CampaignApiKey>(`/integrations/api-keys/${apiKeyId}/rotate`, {
      method: 'POST',
    });
  }

  async deleteIntegrationApiKey(apiKeyId: number): Promise<void> {
    return this.request<void>(`/integrations/api-keys/${apiKeyId}`, {
      method: 'DELETE',
    });
  }

  async getIntegrationProvidersScaffold(): Promise<ParityScaffoldList<IntegrationProvider>> {
    return this.getIntegrationProviders();
  }

  async getIntegrationWebhooksScaffold(): Promise<ParityScaffoldList<CampaignWebhook>> {
    return this.getIntegrationWebhooks();
  }

  async getIntegrationApiKeysScaffold(): Promise<ParityScaffoldList<CampaignApiKey>> {
    return this.getIntegrationApiKeys();
  }

  async getCampaignMembers(campaignId: number): Promise<CampaignMember[]> {
    return this.request<CampaignMember[]>(`/campaigns/${campaignId}/members`);
  }

  async addCampaignMember(
    campaignId: number,
    username: string,
    role: 'gm' | 'player' | 'viewer' = 'player'
  ): Promise<CampaignMember> {
    return this.request<CampaignMember>(`/campaigns/${campaignId}/members`, {
      method: 'POST',
      body: JSON.stringify({ username, role }),
    });
  }

  async provisionCampaignMember(
    campaignId: number,
    input: {
      username: string;
      role?: 'gm' | 'player' | 'viewer';
      password?: string;
    }
  ): Promise<CampaignMemberProvisionResult> {
    return this.request<CampaignMemberProvisionResult>(`/campaigns/${campaignId}/members/provision`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateCampaignMember(
    campaignId: number,
    memberId: number,
    role: 'gm' | 'player' | 'viewer'
  ): Promise<CampaignMember> {
    return this.request<CampaignMember>(`/campaigns/${campaignId}/members/${memberId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    });
  }

  async removeCampaignMember(campaignId: number, memberId: number): Promise<void> {
    return this.request<void>(`/campaigns/${campaignId}/members/${memberId}`, {
      method: 'DELETE',
    });
  }

  async resetCampaignMemberPassword(
    campaignId: number,
    memberId: number,
    newPassword: string
  ): Promise<{ member: CampaignMember; passwordUpdated: boolean }> {
    return this.request<{ member: CampaignMember; passwordUpdated: boolean }>(
      `/campaigns/${campaignId}/members/${memberId}/reset-password`,
      {
        method: 'POST',
        body: JSON.stringify({ newPassword }),
      }
    );
  }

  async changeMyCampaignPassword(
    campaignId: number,
    currentPassword: string,
    newPassword: string
  ): Promise<{ changed: boolean }> {
    return this.request<{ changed: boolean }>(`/campaigns/${campaignId}/me/change-password`, {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  async getCampaignAccessPolicy(campaignId: number): Promise<CampaignAccessPolicy> {
    return this.request<CampaignAccessPolicy>(`/campaigns/${campaignId}/access-policy`);
  }

  async getCampaignRegistrationAllowlist(
    campaignId: number
  ): Promise<{ items: RegistrationAllowlistEntry[]; total: number }> {
    return this.request<{ items: RegistrationAllowlistEntry[]; total: number }>(
      `/campaigns/${campaignId}/registration-allowlist`
    );
  }

  async addCampaignRegistrationAllowlistEntry(
    campaignId: number,
    username: string
  ): Promise<RegistrationAllowlistEntry> {
    return this.request<RegistrationAllowlistEntry>(`/campaigns/${campaignId}/registration-allowlist`, {
      method: 'POST',
      body: JSON.stringify({ username }),
    });
  }

  async removeCampaignRegistrationAllowlistEntry(campaignId: number, username: string): Promise<void> {
    return this.request<void>(
      `/campaigns/${campaignId}/registration-allowlist/${encodeURIComponent(username)}`,
      {
        method: 'DELETE',
      }
    );
  }

  // Entity shares
  async getEntityShares(entityType: 'wikiArticles' | 'sessionNotes', entityId: number): Promise<EntityShare[]> {
    return this.request<EntityShare[]>(`/campaign/shares/${entityType}/${entityId}`);
  }

  async addEntityShare(
    entityType: 'wikiArticles' | 'sessionNotes',
    entityId: number,
    username: string
  ): Promise<EntityShare> {
    return this.request<EntityShare>(`/campaign/shares/${entityType}/${entityId}`, {
      method: 'POST',
      body: JSON.stringify({ username }),
    });
  }

  async removeEntityShare(
    entityType: 'wikiArticles' | 'sessionNotes',
    entityId: number,
    userId: number
  ): Promise<void> {
    return this.request<void>(`/campaign/shares/${entityType}/${entityId}/${userId}`, {
      method: 'DELETE',
    });
  }

  async getShareGroups(): Promise<ShareGroup[]> {
    return this.request<ShareGroup[]>('/campaign/share-groups');
  }

  async createShareGroup(name: string, description = ''): Promise<ShareGroup> {
    return this.request<ShareGroup>('/campaign/share-groups', {
      method: 'POST',
      body: JSON.stringify({ name, description }),
    });
  }

  async updateShareGroup(
    groupId: number,
    updates: { name?: string; description?: string }
  ): Promise<ShareGroup> {
    return this.request<ShareGroup>(`/campaign/share-groups/${groupId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteShareGroup(groupId: number): Promise<void> {
    return this.request<void>(`/campaign/share-groups/${groupId}`, {
      method: 'DELETE',
    });
  }

  async getShareGroupMembers(groupId: number): Promise<EntityShare[]> {
    return this.request<EntityShare[]>(`/campaign/share-groups/${groupId}/members`);
  }

  async addShareGroupMember(groupId: number, username: string): Promise<EntityShare> {
    return this.request<EntityShare>(`/campaign/share-groups/${groupId}/members`, {
      method: 'POST',
      body: JSON.stringify({ username }),
    });
  }

  async removeShareGroupMember(groupId: number, userId: number): Promise<void> {
    return this.request<void>(`/campaign/share-groups/${groupId}/members/${userId}`, {
      method: 'DELETE',
    });
  }

  async getEntityShareGroups(
    entityType: 'wikiArticles' | 'sessionNotes',
    entityId: number
  ): Promise<ShareGroup[]> {
    return this.request<ShareGroup[]>(`/campaign/shares/${entityType}/${entityId}/groups`);
  }

  async addEntityShareGroup(
    entityType: 'wikiArticles' | 'sessionNotes',
    entityId: number,
    groupId: number
  ): Promise<ShareGroup> {
    return this.request<ShareGroup>(`/campaign/shares/${entityType}/${entityId}/groups`, {
      method: 'POST',
      body: JSON.stringify({ groupId }),
    });
  }

  async removeEntityShareGroup(
    entityType: 'wikiArticles' | 'sessionNotes',
    entityId: number,
    groupId: number
  ): Promise<void> {
    return this.request<void>(`/campaign/shares/${entityType}/${entityId}/groups/${groupId}`, {
      method: 'DELETE',
    });
  }

  // Campaign methods
  async getCampaign(): Promise<CampaignData> {
    return this.request<CampaignData>('/campaign');
  }

  // Session runtime (Phase 3)
  async getSessionAgendaItems(sessionNoteId?: number): Promise<SessionAgendaItem[]> {
    const query = sessionNoteId != null ? `?sessionNoteId=${sessionNoteId}` : '';
    return this.request<SessionAgendaItem[]>(`/campaign/session-agenda-items${query}`);
  }

  async createSessionAgendaItem(
    data: Omit<SessionAgendaItem, 'id'>
  ): Promise<SessionAgendaItem> {
    return this.request<SessionAgendaItem>('/campaign/session-agenda-items', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSessionAgendaItem(
    id: number,
    updates: Partial<Omit<SessionAgendaItem, 'id'>>
  ): Promise<SessionAgendaItem> {
    return this.request<SessionAgendaItem>(`/campaign/session-agenda-items/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteSessionAgendaItem(id: number): Promise<void> {
    return this.request<void>(`/campaign/session-agenda-items/${id}`, { method: 'DELETE' });
  }

  async getSessionPrepTasks(sessionNoteId?: number): Promise<SessionPrepTask[]> {
    const query = sessionNoteId != null ? `?sessionNoteId=${sessionNoteId}` : '';
    return this.request<SessionPrepTask[]>(`/campaign/session-prep-tasks${query}`);
  }

  async createSessionPrepTask(
    data: Omit<SessionPrepTask, 'id'>
  ): Promise<SessionPrepTask> {
    return this.request<SessionPrepTask>('/campaign/session-prep-tasks', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSessionPrepTask(
    id: number,
    updates: Partial<Omit<SessionPrepTask, 'id'>>
  ): Promise<SessionPrepTask> {
    return this.request<SessionPrepTask>(`/campaign/session-prep-tasks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteSessionPrepTask(id: number): Promise<void> {
    return this.request<void>(`/campaign/session-prep-tasks/${id}`, { method: 'DELETE' });
  }

  async getSessionEncounters(sessionNoteId?: number): Promise<SessionEncounter[]> {
    const query = sessionNoteId != null ? `?sessionNoteId=${sessionNoteId}` : '';
    return this.request<SessionEncounter[]>(`/campaign/session-encounters${query}`);
  }

  async createSessionEncounter(
    data: Omit<SessionEncounter, 'id'>
  ): Promise<SessionEncounter> {
    return this.request<SessionEncounter>('/campaign/session-encounters', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSessionEncounter(
    id: number,
    updates: Partial<Omit<SessionEncounter, 'id'>>
  ): Promise<SessionEncounter> {
    return this.request<SessionEncounter>(`/campaign/session-encounters/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteSessionEncounter(id: number): Promise<void> {
    return this.request<void>(`/campaign/session-encounters/${id}`, { method: 'DELETE' });
  }

  async getSessionRecapItems(sessionNoteId?: number): Promise<SessionRecapItem[]> {
    const query = sessionNoteId != null ? `?sessionNoteId=${sessionNoteId}` : '';
    return this.request<SessionRecapItem[]>(`/campaign/session-recap-items${query}`);
  }

  async createSessionRecapItem(
    data: Omit<SessionRecapItem, 'id'>
  ): Promise<SessionRecapItem> {
    return this.request<SessionRecapItem>('/campaign/session-recap-items', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSessionRecapItem(
    id: number,
    updates: Partial<Omit<SessionRecapItem, 'id'>>
  ): Promise<SessionRecapItem> {
    return this.request<SessionRecapItem>(`/campaign/session-recap-items/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteSessionRecapItem(id: number): Promise<void> {
    return this.request<void>(`/campaign/session-recap-items/${id}`, { method: 'DELETE' });
  }

  async getCampaignCastMembers(): Promise<CampaignCastMember[]> {
    return this.request<CampaignCastMember[]>('/campaign/campaign-cast-members');
  }

  async createCampaignCastMember(
    data: Omit<CampaignCastMember, 'id'>
  ): Promise<CampaignCastMember> {
    return this.request<CampaignCastMember>('/campaign/campaign-cast-members', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCampaignCastMember(
    id: number,
    updates: Partial<Omit<CampaignCastMember, 'id'>>
  ): Promise<CampaignCastMember> {
    return this.request<CampaignCastMember>(`/campaign/campaign-cast-members/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteCampaignCastMember(id: number): Promise<void> {
    return this.request<void>(`/campaign/campaign-cast-members/${id}`, { method: 'DELETE' });
  }

  async getStatblocks(): Promise<StatblockEntry[]> {
    return this.request<StatblockEntry[]>('/campaign/statblocks');
  }

  async createStatblock(
    data: Omit<StatblockEntry, 'id'>
  ): Promise<StatblockEntry> {
    return this.request<StatblockEntry>('/campaign/statblocks', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateStatblock(
    id: number,
    updates: Partial<Omit<StatblockEntry, 'id'>>
  ): Promise<StatblockEntry> {
    return this.request<StatblockEntry>(`/campaign/statblocks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteStatblock(id: number): Promise<void> {
    return this.request<void>(`/campaign/statblocks/${id}`, { method: 'DELETE' });
  }

  async getStatblockVersions(id: number, limit?: number): Promise<StatblockEntryVersion[]> {
    const query = limit != null ? `?limit=${limit}` : '';
    return this.request<StatblockEntryVersion[]>(`/campaign/statblocks/${id}/versions${query}`);
  }

  async restoreStatblockVersion(
    id: number,
    versionNumber: number
  ): Promise<StatblockVersionRestoreResponse> {
    return this.request<StatblockVersionRestoreResponse>(
      `/campaign/statblocks/${id}/versions/${versionNumber}/restore`,
      { method: 'POST' }
    );
  }

  async getEncounterStatblocks(sessionEncounterId?: number): Promise<EncounterStatblockLink[]> {
    const query = sessionEncounterId != null ? `?sessionEncounterId=${sessionEncounterId}` : '';
    return this.request<EncounterStatblockLink[]>(`/campaign/encounter-statblocks${query}`);
  }

  async createEncounterStatblock(
    data: Omit<EncounterStatblockLink, 'id'>
  ): Promise<EncounterStatblockLink> {
    return this.request<EncounterStatblockLink>('/campaign/encounter-statblocks', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateEncounterStatblock(
    id: number,
    updates: Partial<Omit<EncounterStatblockLink, 'id'>>
  ): Promise<EncounterStatblockLink> {
    return this.request<EncounterStatblockLink>(`/campaign/encounter-statblocks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteEncounterStatblock(id: number): Promise<void> {
    return this.request<void>(`/campaign/encounter-statblocks/${id}`, { method: 'DELETE' });
  }

  async getGmScreenPins(columnKey?: string): Promise<GmScreenPin[]> {
    const query = columnKey ? `?columnKey=${encodeURIComponent(columnKey)}` : '';
    return this.request<GmScreenPin[]>(`/campaign/gm-screen-pins${query}`);
  }

  async createGmScreenPin(
    data: Omit<GmScreenPin, 'id'>
  ): Promise<GmScreenPin> {
    return this.request<GmScreenPin>('/campaign/gm-screen-pins', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateGmScreenPin(
    id: number,
    updates: Partial<Omit<GmScreenPin, 'id'>>
  ): Promise<GmScreenPin> {
    return this.request<GmScreenPin>(`/campaign/gm-screen-pins/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteGmScreenPin(id: number): Promise<void> {
    return this.request<void>(`/campaign/gm-screen-pins/${id}`, { method: 'DELETE' });
  }

  // Maps + timelines
  async getMaps(): Promise<WorldMap[]> {
    return this.request<WorldMap[]>('/campaign/maps');
  }

  async createMap(data: Omit<WorldMap, 'id'>): Promise<WorldMap> {
    return this.request<WorldMap>('/campaign/maps', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMap(id: number, updates: Partial<WorldMap>): Promise<WorldMap> {
    return this.request<WorldMap>(`/campaign/maps/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteMap(id: number): Promise<void> {
    return this.request<void>(`/campaign/maps/${id}`, { method: 'DELETE' });
  }

  async getMapLayers(mapId?: number): Promise<MapLayer[]> {
    const query = mapId != null ? `?mapId=${mapId}` : '';
    return this.request<MapLayer[]>(`/campaign/map-layers${query}`);
  }

  async createMapLayer(data: Omit<MapLayer, 'id'>): Promise<MapLayer> {
    return this.request<MapLayer>('/campaign/map-layers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMapLayer(id: number, updates: Partial<MapLayer>): Promise<MapLayer> {
    return this.request<MapLayer>(`/campaign/map-layers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async publishMapLayerReveal(
    id: number,
    payload: { revealState?: 'private' | 'players' | 'public' } = {}
  ): Promise<MapLayer> {
    return this.request<MapLayer>(`/campaign/map-layers/${id}/publish-reveal`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async deleteMapLayer(id: number): Promise<void> {
    return this.request<void>(`/campaign/map-layers/${id}`, { method: 'DELETE' });
  }

  async getMapMarkers(mapId?: number): Promise<MapMarker[]> {
    const query = mapId != null ? `?mapId=${mapId}` : '';
    return this.request<MapMarker[]>(`/campaign/map-markers${query}`);
  }

  async createMapMarker(data: Omit<MapMarker, 'id'>): Promise<MapMarker> {
    return this.request<MapMarker>('/campaign/map-markers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMapMarker(id: number, updates: Partial<MapMarker>): Promise<MapMarker> {
    return this.request<MapMarker>(`/campaign/map-markers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteMapMarker(id: number): Promise<void> {
    return this.request<void>(`/campaign/map-markers/${id}`, { method: 'DELETE' });
  }

  async getTimelines(): Promise<Timeline[]> {
    return this.request<Timeline[]>('/campaign/timelines');
  }

  async createTimeline(data: Omit<Timeline, 'id'>): Promise<Timeline> {
    return this.request<Timeline>('/campaign/timelines', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTimeline(id: number, updates: Partial<Timeline>): Promise<Timeline> {
    return this.request<Timeline>(`/campaign/timelines/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteTimeline(id: number): Promise<void> {
    return this.request<void>(`/campaign/timelines/${id}`, { method: 'DELETE' });
  }

  async getTimelineEvents(timelineId?: number): Promise<TimelineEvent[]> {
    const query = timelineId != null ? `?timelineId=${timelineId}` : '';
    return this.request<TimelineEvent[]>(`/campaign/timeline-events${query}`);
  }

  async getChroniclePlayback(options: {
    timelineId: number;
    fromSortIndex?: number;
    toSortIndex?: number;
    limit?: number;
    offset?: number;
  }): Promise<ChroniclePlaybackResponse> {
    const params = new URLSearchParams();
    params.set('timelineId', String(options.timelineId));
    if (options.fromSortIndex !== undefined) params.set('fromSortIndex', String(options.fromSortIndex));
    if (options.toSortIndex !== undefined) params.set('toSortIndex', String(options.toSortIndex));
    if (options.limit !== undefined) params.set('limit', String(options.limit));
    if (options.offset !== undefined) params.set('offset', String(options.offset));
    return this.request<ChroniclePlaybackResponse>(`/campaign/chronicles/playback?${params.toString()}`);
  }

  async createTimelineEvent(data: Omit<TimelineEvent, 'id'>): Promise<TimelineEvent> {
    return this.request<TimelineEvent>('/campaign/timeline-events', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTimelineEvent(id: number, updates: Partial<TimelineEvent>): Promise<TimelineEvent> {
    return this.request<TimelineEvent>(`/campaign/timeline-events/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteTimelineEvent(id: number): Promise<void> {
    return this.request<void>(`/campaign/timeline-events/${id}`, { method: 'DELETE' });
  }

  // Phase 4 visual structures: family trees + diplomacy webs
  async getFamilyTrees(): Promise<FamilyTree[]> {
    return this.request<FamilyTree[]>('/campaign/family-trees');
  }

  async createFamilyTree(data: Omit<FamilyTree, 'id'>): Promise<FamilyTree> {
    return this.request<FamilyTree>('/campaign/family-trees', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateFamilyTree(id: number, updates: Partial<Omit<FamilyTree, 'id'>>): Promise<FamilyTree> {
    return this.request<FamilyTree>(`/campaign/family-trees/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteFamilyTree(id: number): Promise<void> {
    return this.request<void>(`/campaign/family-trees/${id}`, { method: 'DELETE' });
  }

  async getFamilyTreeNodes(familyTreeId?: number): Promise<FamilyTreeNode[]> {
    const query = familyTreeId != null ? `?familyTreeId=${familyTreeId}` : '';
    return this.request<FamilyTreeNode[]>(`/campaign/family-tree-nodes${query}`);
  }

  async createFamilyTreeNode(data: Omit<FamilyTreeNode, 'id'>): Promise<FamilyTreeNode> {
    return this.request<FamilyTreeNode>('/campaign/family-tree-nodes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateFamilyTreeNode(
    id: number,
    updates: Partial<Omit<FamilyTreeNode, 'id'>>
  ): Promise<FamilyTreeNode> {
    return this.request<FamilyTreeNode>(`/campaign/family-tree-nodes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteFamilyTreeNode(id: number): Promise<void> {
    return this.request<void>(`/campaign/family-tree-nodes/${id}`, { method: 'DELETE' });
  }

  async getFamilyTreeEdges(familyTreeId?: number): Promise<FamilyTreeEdge[]> {
    const query = familyTreeId != null ? `?familyTreeId=${familyTreeId}` : '';
    return this.request<FamilyTreeEdge[]>(`/campaign/family-tree-edges${query}`);
  }

  async createFamilyTreeEdge(data: Omit<FamilyTreeEdge, 'id'>): Promise<FamilyTreeEdge> {
    return this.request<FamilyTreeEdge>('/campaign/family-tree-edges', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateFamilyTreeEdge(
    id: number,
    updates: Partial<Omit<FamilyTreeEdge, 'id'>>
  ): Promise<FamilyTreeEdge> {
    return this.request<FamilyTreeEdge>(`/campaign/family-tree-edges/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteFamilyTreeEdge(id: number): Promise<void> {
    return this.request<void>(`/campaign/family-tree-edges/${id}`, { method: 'DELETE' });
  }

  async getDiplomacyWebs(): Promise<DiplomacyWeb[]> {
    return this.request<DiplomacyWeb[]>('/campaign/diplomacy-webs');
  }

  async createDiplomacyWeb(data: Omit<DiplomacyWeb, 'id'>): Promise<DiplomacyWeb> {
    return this.request<DiplomacyWeb>('/campaign/diplomacy-webs', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateDiplomacyWeb(id: number, updates: Partial<Omit<DiplomacyWeb, 'id'>>): Promise<DiplomacyWeb> {
    return this.request<DiplomacyWeb>(`/campaign/diplomacy-webs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteDiplomacyWeb(id: number): Promise<void> {
    return this.request<void>(`/campaign/diplomacy-webs/${id}`, { method: 'DELETE' });
  }

  async getDiplomacyWebNodes(diplomacyWebId?: number): Promise<DiplomacyWebNode[]> {
    const query = diplomacyWebId != null ? `?diplomacyWebId=${diplomacyWebId}` : '';
    return this.request<DiplomacyWebNode[]>(`/campaign/diplomacy-web-nodes${query}`);
  }

  async createDiplomacyWebNode(data: Omit<DiplomacyWebNode, 'id'>): Promise<DiplomacyWebNode> {
    return this.request<DiplomacyWebNode>('/campaign/diplomacy-web-nodes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateDiplomacyWebNode(
    id: number,
    updates: Partial<Omit<DiplomacyWebNode, 'id'>>
  ): Promise<DiplomacyWebNode> {
    return this.request<DiplomacyWebNode>(`/campaign/diplomacy-web-nodes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteDiplomacyWebNode(id: number): Promise<void> {
    return this.request<void>(`/campaign/diplomacy-web-nodes/${id}`, { method: 'DELETE' });
  }

  async getDiplomacyWebEdges(diplomacyWebId?: number): Promise<DiplomacyWebEdge[]> {
    const query = diplomacyWebId != null ? `?diplomacyWebId=${diplomacyWebId}` : '';
    return this.request<DiplomacyWebEdge[]>(`/campaign/diplomacy-web-edges${query}`);
  }

  async createDiplomacyWebEdge(data: Omit<DiplomacyWebEdge, 'id'>): Promise<DiplomacyWebEdge> {
    return this.request<DiplomacyWebEdge>('/campaign/diplomacy-web-edges', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateDiplomacyWebEdge(
    id: number,
    updates: Partial<Omit<DiplomacyWebEdge, 'id'>>
  ): Promise<DiplomacyWebEdge> {
    return this.request<DiplomacyWebEdge>(`/campaign/diplomacy-web-edges/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteDiplomacyWebEdge(id: number): Promise<void> {
    return this.request<void>(`/campaign/diplomacy-web-edges/${id}`, { method: 'DELETE' });
  }

  async getWhiteboards(): Promise<Whiteboard[]> {
    return this.request<Whiteboard[]>('/campaign/whiteboards');
  }

  async createWhiteboard(data: Omit<Whiteboard, 'id'>): Promise<Whiteboard> {
    return this.request<Whiteboard>('/campaign/whiteboards', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateWhiteboard(id: number, updates: Partial<Omit<Whiteboard, 'id'>>): Promise<Whiteboard> {
    return this.request<Whiteboard>(`/campaign/whiteboards/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteWhiteboard(id: number): Promise<void> {
    return this.request<void>(`/campaign/whiteboards/${id}`, { method: 'DELETE' });
  }

  async getWhiteboardNodes(whiteboardId?: number): Promise<WhiteboardNode[]> {
    const query = whiteboardId != null ? `?whiteboardId=${whiteboardId}` : '';
    return this.request<WhiteboardNode[]>(`/campaign/whiteboard-nodes${query}`);
  }

  async createWhiteboardNode(data: Omit<WhiteboardNode, 'id'>): Promise<WhiteboardNode> {
    return this.request<WhiteboardNode>('/campaign/whiteboard-nodes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateWhiteboardNode(
    id: number,
    updates: Partial<Omit<WhiteboardNode, 'id'>>
  ): Promise<WhiteboardNode> {
    return this.request<WhiteboardNode>(`/campaign/whiteboard-nodes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteWhiteboardNode(id: number): Promise<void> {
    return this.request<void>(`/campaign/whiteboard-nodes/${id}`, { method: 'DELETE' });
  }

  async getWhiteboardEdges(whiteboardId?: number): Promise<WhiteboardEdge[]> {
    const query = whiteboardId != null ? `?whiteboardId=${whiteboardId}` : '';
    return this.request<WhiteboardEdge[]>(`/campaign/whiteboard-edges${query}`);
  }

  async createWhiteboardEdge(data: Omit<WhiteboardEdge, 'id'>): Promise<WhiteboardEdge> {
    return this.request<WhiteboardEdge>('/campaign/whiteboard-edges', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateWhiteboardEdge(
    id: number,
    updates: Partial<Omit<WhiteboardEdge, 'id'>>
  ): Promise<WhiteboardEdge> {
    return this.request<WhiteboardEdge>(`/campaign/whiteboard-edges/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteWhiteboardEdge(id: number): Promise<void> {
    return this.request<void>(`/campaign/whiteboard-edges/${id}`, { method: 'DELETE' });
  }

  async getContentTrees(): Promise<ContentTree[]> {
    return this.request<ContentTree[]>('/campaign/content-trees');
  }

  async createContentTree(data: Omit<ContentTree, 'id'>): Promise<ContentTree> {
    return this.request<ContentTree>('/campaign/content-trees', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateContentTree(id: number, updates: Partial<Omit<ContentTree, 'id'>>): Promise<ContentTree> {
    return this.request<ContentTree>(`/campaign/content-trees/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteContentTree(id: number): Promise<void> {
    return this.request<void>(`/campaign/content-trees/${id}`, { method: 'DELETE' });
  }

  async getContentTreeNodes(contentTreeId?: number): Promise<ContentTreeNode[]> {
    const query = contentTreeId != null ? `?contentTreeId=${contentTreeId}` : '';
    return this.request<ContentTreeNode[]>(`/campaign/content-tree-nodes${query}`);
  }

  async createContentTreeNode(data: Omit<ContentTreeNode, 'id'>): Promise<ContentTreeNode> {
    return this.request<ContentTreeNode>('/campaign/content-tree-nodes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateContentTreeNode(
    id: number,
    updates: Partial<Omit<ContentTreeNode, 'id'>>
  ): Promise<ContentTreeNode> {
    return this.request<ContentTreeNode>(`/campaign/content-tree-nodes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteContentTreeNode(id: number): Promise<void> {
    return this.request<void>(`/campaign/content-tree-nodes/${id}`, { method: 'DELETE' });
  }

  async getWikiTemplates(): Promise<WikiTemplate[]> {
    return this.request<WikiTemplate[]>('/campaign/wiki-templates');
  }

  async getWikiArticleBacklinks(
    articleId: number,
    options?: {
      limit?: number;
      offset?: number;
      resolved?: boolean;
      sourceType?: 'all' | 'wikiArticles';
    }
  ): Promise<WikiBacklinkPage> {
    const params = new URLSearchParams();
    if (options?.limit !== undefined) params.set('limit', String(options.limit));
    if (options?.offset !== undefined) params.set('offset', String(options.offset));
    if (options?.resolved !== undefined) params.set('resolved', String(options.resolved));
    if (options?.sourceType) params.set('sourceType', options.sourceType);

    const query = params.toString();
    return this.request<WikiBacklinkPage>(
      `/campaign/wiki-articles/${articleId}/backlinks${query ? `?${query}` : ''}`
    );
  }

  async getRelationshipEdges(
    nodeType: string,
    nodeId: number,
    options?: {
      direction?: RelationshipDirection;
      relationType?: string;
    }
  ): Promise<RelationshipEdge[]> {
    const params = new URLSearchParams();
    params.set('nodeType', nodeType);
    params.set('nodeId', String(nodeId));
    if (options?.direction) params.set('direction', options.direction);
    if (options?.relationType) params.set('relationType', options.relationType);

    return this.request<RelationshipEdge[]>(`/campaign/relationships?${params.toString()}`);
  }

  async getContentRevisions(
    entityType: 'session_notes' | 'wiki_articles' | 'sessionNotes' | 'wikiArticles',
    entityId: number,
    options?: {
      limit?: number;
    }
  ): Promise<ContentRevision[]> {
    const params = new URLSearchParams();
    if (options?.limit !== undefined) params.set('limit', String(options.limit));
    const query = params.toString();
    return this.request<ContentRevision[]>(
      `/campaign/revisions/${entityType}/${entityId}${query ? `?${query}` : ''}`
    );
  }

  async getContentRevisionDiff(
    entityType: 'session_notes' | 'wiki_articles' | 'sessionNotes' | 'wikiArticles',
    entityId: number,
    options?: {
      fromRevision?: number;
      toRevision?: number;
    }
  ): Promise<ContentRevisionDiff> {
    const params = new URLSearchParams();
    if (options?.fromRevision !== undefined) {
      params.set('fromRevision', String(options.fromRevision));
    }
    if (options?.toRevision !== undefined) {
      params.set('toRevision', String(options.toRevision));
    }
    const query = params.toString();
    return this.request<ContentRevisionDiff>(
      `/campaign/revisions/${entityType}/${entityId}/diff${query ? `?${query}` : ''}`
    );
  }

  async restoreContentRevision(
    entityType: 'session_notes' | 'wiki_articles' | 'sessionNotes' | 'wikiArticles',
    entityId: number,
    payload: {
      revisionNumber: number;
      expectedCurrentRevision?: number;
    }
  ): Promise<ContentRestoreResponse> {
    return this.request<ContentRestoreResponse>(
      `/campaign/revisions/${entityType}/${entityId}/restore`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );
  }

  async updateCampaign(data: Partial<CampaignData>): Promise<CampaignData> {
    return this.request<CampaignData>('/campaign', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async exportCampaign(): Promise<ExportData> {
    return this.request<ExportData>('/campaign/export');
  }

  async importCampaign(data: CampaignData): Promise<CampaignData> {
    return this.request<CampaignData>('/campaign/import', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async convertCardsToWikiArticles(): Promise<CardToWikiConversionResult> {
    return this.request<CardToWikiConversionResult>('/campaign/wiki-articles/convert-cards', {
      method: 'POST',
    });
  }

  // Collection CRUD - map frontend keys to API endpoints
  private collectionToEndpoint(collection: CollectionKey): string {
    const map: Record<CollectionKey, string> = {
      playerCharacters: 'player-characters',
      npcs: 'npcs',
      locations: 'locations',
      organizations: 'organizations',
      plotThreads: 'plot-threads',
      sessionNotes: 'session-notes',
      calendars: 'calendars',
      calendarEvents: 'calendar-events',
      futureContent: 'future-content',
      wikiArticles: 'wiki-articles',
    };
    return map[collection];
  }

  async getItems<K extends CollectionKey>(collection: K): Promise<CollectionItem<K>[]> {
    const endpoint = this.collectionToEndpoint(collection);
    return this.request<CollectionItem<K>[]>(`/campaign/${endpoint}`);
  }

  async createItem<K extends CollectionKey>(
    collection: K,
    item: NewCollectionItem<K>
  ): Promise<CollectionItem<K>> {
    const endpoint = this.collectionToEndpoint(collection);
    return this.request<CollectionItem<K>>(`/campaign/${endpoint}`, {
      method: 'POST',
      body: JSON.stringify(item),
    });
  }

  async updateItem<K extends CollectionKey>(
    collection: K,
    id: number,
    updates: Partial<CollectionItem<K>>
  ): Promise<CollectionItem<K>> {
    const endpoint = this.collectionToEndpoint(collection);
    return this.request<CollectionItem<K>>(`/campaign/${endpoint}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteItem<K extends CollectionKey>(collection: K, id: number): Promise<void> {
    const endpoint = this.collectionToEndpoint(collection);
    return this.request<void>(`/campaign/${endpoint}/${id}`, {
      method: 'DELETE',
    });
  }

  // Faction relationships
  async addFactionRelationship(
    pcId: number,
    faction: string,
    renown: number
  ): Promise<FactionRelationship> {
    return this.request<FactionRelationship>(
      `/campaign/player-characters/${pcId}/faction-relationships`,
      {
        method: 'POST',
        body: JSON.stringify({ faction, renown }),
      }
    );
  }

  async updateFactionRelationship(
    pcId: number,
    relationshipId: number,
    renown: number
  ): Promise<FactionRelationship> {
    return this.request<FactionRelationship>(
      `/campaign/player-characters/${pcId}/faction-relationships/${relationshipId}`,
      {
        method: 'PUT',
        body: JSON.stringify({ renown }),
      }
    );
  }

  async deleteFactionRelationship(pcId: number, relationshipId: number): Promise<void> {
    return this.request<void>(
      `/campaign/player-characters/${pcId}/faction-relationships/${relationshipId}`,
      {
        method: 'DELETE',
      }
    );
  }

  // Session linked items
  async addSessionLinkedItem(
    sessionId: number,
    category: string,
    itemId: number
  ): Promise<SessionLinkedItem> {
    return this.request<SessionLinkedItem>(
      `/campaign/session-notes/${sessionId}/linked-items`,
      {
        method: 'POST',
        body: JSON.stringify({ category, itemId }),
      }
    );
  }

  async removeSessionLinkedItem(sessionId: number, linkId: number): Promise<void> {
    return this.request<void>(
      `/campaign/session-notes/${sessionId}/linked-items/${linkId}`,
      {
        method: 'DELETE',
      }
    );
  }

  // AI assistant
  async sendAssistantMessage(
    messages: AssistantMessage[],
    temperature = 0.8,
    options?: AssistantChatOptions
  ): Promise<AssistantChatResult> {
    const response = await this.request<{
      content: string;
      toolCalls?: AssistantToolCallResult[];
      threadId?: number;
    }>('/assistant/chat', {
      method: 'POST',
      body: JSON.stringify({ messages, temperature, ...options }),
    });
    return {
      content: response.content,
      toolCalls: response.toolCalls || [],
      threadId: response.threadId,
    };
  }

  // AI assistant with streaming (enhanced with tool calls + server context)
  async streamAssistantMessage(
    messages: AssistantMessage[],
    onChunk: (chunk: string) => void,
    temperature = 0.8,
    options?: AssistantChatOptions & {
      onToolCalls?: (toolCalls: AssistantToolCallResult[]) => void;
      onAgentStatus?: (status: string, round: number) => void;
    }
  ): Promise<AssistantStreamResult> {
    const token = this.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const { onToolCalls, onAgentStatus, ...chatOptions } = options || {};

    const response = await fetch(`${API_BASE}/assistant/chat/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ messages, temperature, ...chatOptions }),
    });

    if (!response.ok) {
      throw new Error('Failed to start streaming');
    }

    if (!response.body) {
      throw new Error('No response body');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullContent = '';
    let buffer = '';
    let toolCalls: AssistantToolCallResult[] = [];
    let threadId: number | undefined;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        let newlineIndex = buffer.indexOf('\n');
        while (newlineIndex !== -1) {
          const rawLine = buffer.slice(0, newlineIndex).replace(/\r$/, '');
          buffer = buffer.slice(newlineIndex + 1);

          if (rawLine.startsWith('data: ')) {
            const data = rawLine.slice(6).trim();
            if (data !== '[DONE]') {
              try {
                const parsed = JSON.parse(data) as {
                  content?: string;
                  error?: string;
                  toolCalls?: AssistantToolCallResult[];
                  threadId?: number;
                  agentStatus?: string;
                  round?: number;
                };
                if (parsed.error) {
                  throw new Error(parsed.error);
                }
                if (parsed.threadId) {
                  threadId = parsed.threadId;
                }
                if (parsed.content) {
                  fullContent += parsed.content;
                  onChunk(parsed.content);
                }
                if (parsed.toolCalls) {
                  toolCalls = parsed.toolCalls;
                  onToolCalls?.(parsed.toolCalls);
                }
                if (parsed.agentStatus) {
                  onAgentStatus?.(parsed.agentStatus, parsed.round ?? 0);
                }
              } catch (error) {
                if (!(error instanceof SyntaxError)) {
                  throw error;
                }
              }
            }
          }

          newlineIndex = buffer.indexOf('\n');
        }
      }

      const remaining = (buffer + decoder.decode()).trim();
      if (remaining.startsWith('data: ')) {
        const data = remaining.slice(6).trim();
        if (data && data !== '[DONE]') {
          try {
            const parsed = JSON.parse(data) as {
              content?: string;
              error?: string;
              toolCalls?: AssistantToolCallResult[];
              threadId?: number;
              agentStatus?: string;
              round?: number;
            };
            if (parsed.error) {
              throw new Error(parsed.error);
            }
            if (parsed.threadId) {
              threadId = parsed.threadId;
            }
            if (parsed.content) {
              fullContent += parsed.content;
              onChunk(parsed.content);
            }
            if (parsed.toolCalls) {
              toolCalls = parsed.toolCalls;
              onToolCalls?.(parsed.toolCalls);
            }
            if (parsed.agentStatus) {
              onAgentStatus?.(parsed.agentStatus, parsed.round ?? 0);
            }
          } catch (error) {
            if (!(error instanceof SyntaxError)) {
              throw error;
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    return { content: fullContent, toolCalls, threadId };
  }

  // ── P2-7: Workflow endpoints ──────────────────────────────────────

  async getWorkflows(): Promise<{ workflows: WorkflowInfo[] }> {
    return this.request<{ workflows: WorkflowInfo[] }>('/assistant/workflows');
  }

  async runWorkflow(
    workflowId: string,
    onEvent: (event: WorkflowStepEvent) => void,
    model?: string
  ): Promise<void> {
    const token = this.getToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${API_BASE}/assistant/workflows/run`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ workflowId, model }),
    });

    if (!response.ok) throw new Error('Failed to start workflow');
    if (!response.body) throw new Error('No response body');

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        let newlineIndex = buffer.indexOf('\n');
        while (newlineIndex !== -1) {
          const rawLine = buffer.slice(0, newlineIndex).replace(/\r$/, '');
          buffer = buffer.slice(newlineIndex + 1);

          if (rawLine.startsWith('data: ')) {
            const data = rawLine.slice(6).trim();
            if (data !== '[DONE]') {
              try {
                const event = JSON.parse(data) as WorkflowStepEvent;
                onEvent(event);
              } catch { /* skip malformed */ }
            }
          }
          newlineIndex = buffer.indexOf('\n');
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  // ── P2-10: Entity extraction ──────────────────────────────────────

  async extractEntities(text: string): Promise<EntityExtractionResult> {
    return this.request<EntityExtractionResult>('/assistant/extract-entities', {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  }

  // ── P3-12: Natural language search ────────────────────────────────

  async nlSearch(query: string, maxResults?: number): Promise<NLSearchResponse> {
    return this.request<NLSearchResponse>('/assistant/search', {
      method: 'POST',
      body: JSON.stringify({ query, maxResults }),
    });
  }

  // ── Universal CRUD execute + undo ────────────────────────────────

  async executeAssistantOperations(
    operations: Array<{
      action: 'create' | 'update' | 'delete';
      entityType: string;
      entityId?: number;
      data?: Record<string, unknown>;
      parentId?: number;
    }>
  ): Promise<{
    results: Array<{
      success: boolean;
      action: string;
      entityType: string;
      entityId?: number;
      data?: Record<string, unknown>;
      error?: string;
      undoOperationId?: string;
    }>;
  }> {
    return this.request<{
      results: Array<{
        success: boolean;
        action: string;
        entityType: string;
        entityId?: number;
        data?: Record<string, unknown>;
        error?: string;
        undoOperationId?: string;
      }>;
    }>('/assistant/execute', {
      method: 'POST',
      body: JSON.stringify({ operations }),
    });
  }

  async undoAssistantOperation(undoOperationId: string): Promise<{
    success: boolean;
    action: string;
    entityType: string;
    error?: string;
  }> {
    return this.request<{
      success: boolean;
      action: string;
      entityType: string;
      error?: string;
    }>('/assistant/undo', {
      method: 'POST',
      body: JSON.stringify({ undoOperationId }),
    });
  }

  // ── Context Pins ────────────────────────────────────────────────

  async getContextPins(): Promise<{ items: ContextPin[]; total: number }> {
    return this.request<{ items: ContextPin[]; total: number }>('/assistant/context-pins');
  }

  async createContextPin(entityType: string, entityId: number): Promise<ContextPin> {
    return this.request<ContextPin>('/assistant/context-pins', {
      method: 'POST',
      body: JSON.stringify({ entityType, entityId }),
    });
  }

  async deleteContextPin(pinId: number): Promise<void> {
    return this.request<void>(`/assistant/context-pins/${pinId}`, {
      method: 'DELETE',
    });
  }
}

export const api = new ApiClient();
export type { CampaignData, CollectionKey, CollectionItem, NewCollectionItem, User, ExportData, PlayerCharacter };
